import React, { useState, useEffect } from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input, Textarea } from '../../../components/ui/Input';
import { Label } from '../../../components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';

/**
 * Create/Edit Module Modal Component
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Function to close the modal
 * @param {function} onSubmit - Function to handle form submission
 * @param {boolean} loading - Loading state
 * @param {object} editingModule - Module data to edit (null for create mode)
 */
const CreateModuleModal = ({ isOpen, onClose, onSubmit, loading = false, editingModule = null }) => {
  const [formData, setFormData] = useState({
    menumoduleName: '',
    isActive: true
  });
  const [errors, setErrors] = useState({});

  // Update form data when editingModule changes
  useEffect(() => {
    if (editingModule) {
      setFormData({
        menumoduleName: editingModule.menumoduleName || editingModule.moduleName || '',
        isActive: editingModule.isActive !== undefined ? editingModule.isActive : true
      });
    } else {
      setFormData({
        menumoduleName: '',
        isActive: true
      });
    }
    setErrors({});
  }, [editingModule, isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.menumoduleName || formData.menumoduleName.trim() === '') {
      newErrors.menumoduleName = 'Module name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    onSubmit(formData);
  };

  const handleClose = () => {
    setFormData({
      menumoduleName: '',
      isActive: true
    });
    setErrors({});
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={editingModule ? "Edit Module" : "Create Module"} size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="moduleName" className="mb-2 block">
            Module Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="moduleName"
            type="text"
            placeholder="Enter module name"
            value={formData.menumoduleName}
            onChange={(e) => handleChange('menumoduleName', e.target.value)}
            error={errors.menumoduleName}
            required
            disabled={loading}
          />
        </div>

        <div>
          <Label htmlFor="status" className="mb-2 block">
            Status
          </Label>
          <Select
            value={formData.isActive ? 'active' : 'inactive'}
            onValueChange={(value) => handleChange('isActive', value === 'active')}
          >
            <SelectTrigger disabled={loading}>
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="gradient-primary text-primary-foreground"
            disabled={loading}
          >
            {loading ? (editingModule ? 'Updating...' : 'Creating...') : (editingModule ? 'Update Module' : 'Create Module')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateModuleModal;

